import {
	AnimalPawIcon,
	Button,
	NoteIcon,
	BackpackIcon,
	BookOpenIcon,
	ClassIcon,
	SpeciesIcon,
	PuzzleIcon,
	ReadingListIcon,
	ScriptIcon,
	SpellBookIcon,
	Tile,
	WikiIcon,
} from '@kit';

import React from 'react';

import {Layout} from '../../components/layout';
import {SiteSearch} from '../../components/site-search';
import {useUser} from '../../components/use-user';
import {SECTIONS, SectionKey, SectionSeo} from '../../lib/seo';
import {BookGrid} from '../books/book-grid';
import {MapGrid} from '../maps/map-grid';
import {ContinueReading} from './continue-reading';
import {DayCard} from './day-card';
import {MySheets} from './my-sheets';
import {SheetsPromo} from './sheets-promo';

import css from './home.page.module.css';

const BOOKS_LIMIT = 3;

type TileItem = {section: SectionKey; icon: React.ReactNode; text: string};

const GROUPS: Array<{title: string; tiles: TileItem[]}> = [
	{
		title: 'Инструменты',
		tiles: [
			{
				section: 'names',
				icon: <NoteIcon size={24} />,
				text: 'Имена эльфов, дварфов, гномов и других видов из книг',
			},
			{
				section: 'spellbook',
				icon: <SpellBookIcon size={24} />,
				text: 'Случайная книга заклинаний волшебника с ценой',
			},
		],
	},
	{
		title: 'Персонаж',
		tiles: [
			{section: 'classes', icon: <ClassIcon size={24} />, text: 'Особенности по уровням, таблицы и подклассы'},
			{section: 'species', icon: <SpeciesIcon size={24} />, text: 'Тип существа, размер, скорость и особенности'},
			{
				section: 'origins',
				icon: <ScriptIcon size={24} />,
				text: 'Характеристики, черта происхождения и снаряжение',
			},
			{
				section: 'feats',
				icon: <PuzzleIcon size={24} />,
				text: 'Черты происхождения, универсальные, боевые стили, эпические дары и планарный договор',
			},
		],
	},
	{
		title: 'Магия, снаряжение и бестиарий',
		tiles: [
			{section: 'spells', icon: <SpellBookIcon size={24} />, text: 'Фокусы и заклинания 1–9 кругов с фильтрами'},
			{
				section: 'items',
				icon: <BackpackIcon size={24} />,
				text: 'Магические предметы, оружие, доспехи и снаряжение',
			},
			{section: 'monsters', icon: <AnimalPawIcon size={24} />, text: 'Бестиарий с блоками параметров'},
		],
	},
	{
		title: 'Мир',
		tiles: [{section: 'wiki', icon: <WikiIcon size={24} />, text: 'Города, страны и места Забытых Королевств'}],
	},
	{
		title: 'Правила',
		tiles: [
			{
				section: 'glossary',
				icon: <ReadingListIcon size={24} />,
				text: 'Действия, состояния, области и другие термины',
			},
			{section: 'trans14to24', icon: <BookOpenIcon size={24} />, text: 'Как изменились названия в редакции 2024'},
		],
	},
];

const MAPS_LIMIT = 3;

export const HomePage: React.FC = () => {
	const user = useUser();

	return (
		<Layout hideSearch>
			<div className={css.home}>
				<SectionSeo section="index" headingClassName={css.visuallyHidden} />

				<div className={css.search}>
					<SiteSearch />
				</div>

				<div className={css.stack}>
					{user.id ? <MySheets /> : <SheetsPromo />}
					<ContinueReading />
					<DayCard />
				</div>

				{GROUPS.map((group, index) => (
					<section key={group.title} className={css.group} aria-labelledby={`home-group-${index}`}>
						<h2 id={`home-group-${index}`} className={css.groupTitle}>
							{group.title}
						</h2>
						<ul className={css.tiles}>
							{group.tiles.map(({section, icon, text}) => (
								<li key={section}>
									<Tile
										route={SECTIONS[section].route}
										media={icon}
										title={SECTIONS[section].name}
										description={text}
									/>
								</li>
							))}
						</ul>
					</section>
				))}

				<section className={css.group} aria-labelledby="home-group-maps">
					<div className={css.groupHeader}>
						<h2 id="home-group-maps" className={css.groupTitle}>
							{SECTIONS.maps.name}
						</h2>
						<Button link route="maps">
							Все карты
						</Button>
					</div>
					<MapGrid limit={MAPS_LIMIT} />
				</section>

				<section className={css.group} aria-labelledby="home-group-books">
					<div className={css.groupHeader}>
						<h2 id="home-group-books" className={css.groupTitle}>
							{SECTIONS.books.name}
						</h2>
						<Button link route="books">
							Все книги
						</Button>
					</div>
					<BookGrid limit={BOOKS_LIMIT} />
				</section>
			</div>
		</Layout>
	);
};
